import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getTenantId, applyTenantTheme, isLandingPage, getSubdomain } from "@/utils/tenant";
import { tenantApi, TenantInfo } from "@/api/tenant";
import i18n from "@/i18n";
import { readMarketingLanguage } from "@/i18n/marketing";
import { useGradingDraftStore } from './gradingDraftStore'

// 앱 시작 시 잘못된 데이터 정리
const cleanupTenant = () => {
    if (typeof window === "undefined") return;

    const hostname = window.location.hostname;
    const subdomain = getSubdomain(hostname);

    // 서브도메인이 변경되었으면 이전 인증 정보 삭제
    const storedTenant = localStorage.getItem("tenant");
    if (subdomain && storedTenant !== subdomain) {
        console.warn(`[Tenant] Subdomain changed to '${subdomain}'. Clearing auth.`);
        localStorage.setItem("tenant", subdomain);
        localStorage.removeItem("auth-storage");
        localStorage.removeItem("tenant-storage");
    }
    
    // 랜딩 페이지에서는 기존 tenant 정보 유지하지 않음
    if (!subdomain) {
        const stored = localStorage.getItem("tenant");
        if (stored && stored !== "null") {
            console.log(`[Tenant] Landing page - keeping stored tenant for reference: ${stored}`);
        }
    }
};

// 모듈 로드 시 자동 실행
cleanupTenant();

interface TenantState {
    tenantId: string | null;
    tenantInfo: TenantInfo | null;
    isLoading: boolean;
    error: string | null;
    isLanding: boolean;
    // 서브도메인에 해당하는 센터가 없다고 서버가 확인함 (네트워크 오류와 구분)
    notFound: boolean;

    // Actions
    initializeTenant: () => Promise<void>;
    setTenantInfo: (info: TenantInfo) => void;
    clearTenant: () => void;
}

export const useTenantStore = create<TenantState>()(
    persist(
        (set) => ({
            tenantId: null,
            tenantInfo: null,
            isLoading: false,
            error: null,
            isLanding: typeof window !== "undefined" ? isLandingPage() : false,
            notFound: false,

            initializeTenant: async () => {
                // 랜딩 페이지 확인
                if (isLandingPage()) {
                    console.log("[Tenant] Landing page mode - no tenant");
                    i18n.changeLanguage(readMarketingLanguage());
                    set({ tenantId: null, tenantInfo: null, isLoading: false, isLanding: true });
                    return;
                }

                // 서브도메인 기반 테넌트 식별
                const tenantId = getTenantId();

                if (!tenantId) {
                    set({ tenantId: null, tenantInfo: null, isLoading: false, isLanding: true });
                    return;
                }

                // 무조건 로딩 상태로 시작
                set({ tenantId, isLoading: true, error: null, isLanding: false, notFound: false });

                try {
                    console.log(`[Tenant] Fetching info for: ${tenantId}`);
                    const response = await tenantApi.getTenantBySubdomain(tenantId);

                    // 서버는 없는(또는 비활성) 센터를 200 + data:null로 답한다.
                    // 이때 로그인 화면을 그대로 보여 주면 없는 센터에서 로그인한 뒤 센터 API가 모두 실패한다.
                    if (response.data.success && response.data.data == null) {
                        console.warn(`[Tenant] Unknown center subdomain: ${tenantId}`);
                        // 센터 언어 설정이 없으므로 랜딩 페이지와 같은 규칙(기본 한국어)을 따른다.
                        i18n.changeLanguage(readMarketingLanguage());
                        set({ tenantInfo: null, isLoading: false, notFound: true });
                        applyTenantTheme(null);
                        return;
                    }

                    if (response.data.success && response.data.data) {
                        const info = response.data.data;
                        console.log(`[Tenant] Loaded info: ${info.name}`);
                        set({ tenantInfo: info, isLoading: false });
                        applyTenantTheme(info.primaryColor);
                        // 조직 언어 설정 적용
                        const langMode = info.languageMode || "ko";
                        if (langMode === "ko" || langMode === "en") {
                            // 단일 언어 모드: 강제 설정 (localStorage에 저장된 이전 선택 무시)
                            i18n.changeLanguage(langMode);
                        } else if (langMode === "both") {
                            // 양쪽 지원: 저장된 사용자 선택 유지 (LanguageDetector가 localStorage에서 자동 복원)
                        }
                    } else {
                        throw new Error("Tenant data missing in response");
                    }
                } catch (error) {
                    console.error("[Tenant] Load failed:", error);
                    set({
                        tenantInfo: null,
                        isLoading: false,
                        error: "Failed to load tenant",
                    });
                }
            },

            setTenantInfo: (info) => {
                set({ tenantInfo: info });
                applyTenantTheme(info.primaryColor);
            },

            clearTenant: () => {
                set({ tenantId: null, tenantInfo: null });
                applyTenantTheme(null);
            },
        }),
        {
            name: "tenant-storage",
            partialize: (state) => ({
                // tenantId도 저장하지 않음 - 항상 서브도메인에서 계산
                tenantInfo: state.tenantInfo,
            }),
        }
    )
);

// Unsaved grading belongs to the current account and center session.
useTenantStore.subscribe((state, previous) => {
  if (state.tenantId !== previous.tenantId) useGradingDraftStore.getState().clear()
})
